import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  signal,
  viewChild
} from '@angular/core';

import { filter, finalize, tap, withLatestFrom } from 'rxjs/operators';

import {
  OverlayBase,
  TiimeOverlayModule,
  TiimeTimelineModule,
  TiimeSplitButtonModule,
  TiimeButtonModule,
  TiimeMenuModule,
  TiimeIconModule,
  TiimeTooltipModule,
  ButtonComponent
} from 'tiime-material';

import { ContractReminderStatus } from '@gi/enums/contract-reminder-status.enum';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { ContractReminder } from '@gi/models/contract-reminder';
import { DateInput } from '@helpers/date.helper';
import { BusinessUser } from '@models/business-user';
import { ContractFacade } from '@modules/core/facades/contract.facade';
import { ContractUpdateNextReminderDateOverlayService } from '@shared-overlays/contract-update-next-reminder-date-overlay/contract-update-next-reminder-date-overlay.service';
import { AtByPipe } from '@shared-pipes/at-by/at-by.pipe';
import { StatusColorPipe } from '@shared-pipes/status-color/status-color.pipe';
import { StatusLabelPipe } from '@shared-pipes/status-label/status-label.pipe';

import { ContractReminderOverlayData } from './contract-reminder-overlay-data';
import { ContractReminderOverlayReturnedData } from './contract-reminder-overlay-returned-data';
import { ContractReminderOverlayService } from './contract-reminder-overlay.service';

@Component({
  selector: 'app-contract-reminder-overlay',
  standalone: true,
  imports: [
    CommonModule,
    TiimeOverlayModule,
    TiimeTimelineModule,
    TiimeSplitButtonModule,
    TiimeButtonModule,
    TiimeMenuModule,
    TiimeIconModule,
    TiimeTooltipModule,
    StatusColorPipe,
    StatusLabelPipe,
    AtByPipe
  ],
  templateUrl: './contract-reminder-overlay.component.html',
  styleUrls: ['./contract-reminder-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractReminderOverlayComponent
  extends OverlayBase<ContractReminderOverlayReturnedData, ContractReminderOverlayData>
  implements OnInit
{
  readonly overlayAnchor: Signal<ButtonComponent> = viewChild<ButtonComponent>('overlayAnchor');

  readonly reminders: WritableSignal<ContractReminder[]> = signal<ContractReminder[]>([]);
  readonly nextReminderDateModifiedAt: WritableSignal<Date> = signal<Date>(
    this.overlayData().nextReminderDateModifiedAt
  );
  readonly nextReminderDateModifiedBy: WritableSignal<BusinessUser> = signal<BusinessUser>(
    this.overlayData().nextReminderDateModifiedBy
  );

  readonly ContractStatus = ContractStatus;
  readonly ContractReminderStatus = ContractReminderStatus;

  readonly editableReminder: Signal<ContractReminder | null> = computed(
    () =>
      this.reminders()?.find((reminder: ContractReminder) => reminder.status !== ContractReminderStatus.done) ?? null
  );

  constructor(
    private readonly contractFacade: ContractFacade,
    private readonly contractUpdateNextReminderDateOverlayService: ContractUpdateNextReminderDateOverlayService,
    private readonly contractReminderOverlayService: ContractReminderOverlayService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initReminders();
  }

  addReminder(external: boolean): void {
    this.close({ addReminder: true, external });
  }

  openUpdateNextReminderDateOverlay(): void {
    const nextReminderDate = this.editableReminder()?.date;

    this.contractUpdateNextReminderDateOverlayService
      .open(
        {
          nextReminderDate,
          contractId: this.overlayData().contractId
        },
        {
          connectTo: {
            origin: this.overlayAnchor()?.elementRef,
            positions: [
              {
                originX: 'end',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
                offsetY: 5
              }
            ]
          }
        }
      )
      .afterClosed()
      .pipe(
        filter((date: DateInput) => !!date),
        withLatestFrom(this.contractFacade.getBusinessUser()),
        tap(([date, businessUser]: [DateInput, BusinessUser]) => {
          this.nextReminderDateModifiedAt.set(new Date());
          this.nextReminderDateModifiedBy.set(businessUser);
          this.contractReminderOverlayService.updateNextReminderDate({
            contractId: this.overlayData().contractId,
            nextReminderDate: date
          });
          this.initReminders();
        })
      )
      .subscribe();
  }

  private initReminders(): void {
    this.isLoading = true;

    this.contractFacade
      .getReminders(this.overlayData().contractId)
      .pipe(
        tap((reminders: ContractReminder[]) => {
          this.reminders.set(reminders);
          setTimeout(() => this.overlayRef.updatePosition());
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe();
  }
}
